import { useLocation } from 'react-router'
import { useEffect, useRef } from 'react'
import { TrackingData } from '~/services/cookie.server'

type Props = {
  referrer: string | null
  tracking: TrackingData
}

declare const window: Window & { dataLayer?: Record<string, unknown>[] }

/**
 * Pushes pageviews into the GTM dataLayer. The dataLayer itself is created
 * by the inline bootstrap snippet in <Analytics /> before gtm.js loads, so
 * pushes here are queued for GTM no matter how slowly (or whether) the
 * script arrives. The first commit reports `pageview`; client-side
 * navigations report `virtual_pageview` — the GTM container triggers on
 * these two custom events.
 */
export const GoogleTagManager = ({ referrer, tracking }: Props) => {
  const lastPathname = useRef<string>(undefined)
  const { pathname, search } = useLocation()

  useEffect(() => {
    if (pathname === lastPathname.current) return
    const isInitialPageview = lastPathname.current === undefined
    lastPathname.current = pathname

    window.dataLayer ??= []
    window.dataLayer.push({
      ...tracking,
      /**
       * The real origin, so staging and preview traffic is distinguishable
       * from production in analytics. Fragments are omitted to match how GA
       * records page locations.
       */
      page_url: `${window.location.origin}${pathname}${search}`,
      referrer,
      event: isInitialPageview ? 'pageview' : 'virtual_pageview',
    })
  }, [pathname, search, tracking, referrer])

  return null
}
